import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { BreadcrumbHeader, FilterTabs, StatusBadge, EmptyState } from '../../components/shared';
import { stockLevels, serialItems, parts, warehouses } from '../../services/dataClient';
import { useAuth } from '../../auth';
import '../../styles/design-tokens.css';

// ─── Table style constants ─────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const TH = {
  fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-600)',
  backgroundColor: 'var(--grey-100)', py: '10px', px: 2,
  borderBottom: '1px solid var(--grey-200)', whiteSpace: 'nowrap'
};
const TD = {
  fontSize: '0.875rem', color: 'var(--grey-800)',
  py: 0, px: 2, height: 48, borderBottom: '1px solid var(--grey-200)'
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem',
    '& fieldset': { borderColor: 'var(--grey-200)' },
    '&:hover fieldset': { borderColor: 'var(--grey-400)' },
    '&.Mui-focused fieldset': { borderColor: '#009688' }
  }
};

// ─── Part detail modal ─────────────────────────────────────────────────────────
function PartDetailModal({ row, onClose }) {
  if (!row) return null;
  const isLow = row.qtyAvailable < row.reorderThreshold;

  return (
    <Dialog
      open={Boolean(row)}
      onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}}
      maxWidth="sm"
      fullWidth>
      
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)' }}>
            {row.partNumber}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
            {row.description}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1, mt: '-2px' }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pb: 3, pt: '12px !important' }}>
        {/* Stats row */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2.5 }}>
          {[
          { label: 'On Hand', value: row.quantityOnHand },
          { label: 'Available', value: row.qtyAvailable, alert: isLow },
          { label: 'Threshold', value: row.reorderThreshold },
          { label: 'Location', value: row.locationName, wide: true }]
          .map(({ label, value, alert, wide }) =>
          <Box key={label} sx={{ flex: wide ? 2 : 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-500)', mb: '2px' }}>{label}</Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: alert ? '#C62828' : 'var(--grey-900)', lineHeight: 1.2, wordBreak: 'break-word' }}>
                {value}
              </Typography>
            </Box>
          )}
        </Box>

        {isLow &&
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#FFF3E0', border: '1px solid #FFB300', borderRadius: '6px', px: 1.5, py: 1, mb: 2 }}>
            <WarningAmberIcon sx={{ fontSize: 16, color: '#E65100', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.8125rem', color: '#BF360C' }}>
              Stock is below the minimum threshold of {row.reorderThreshold}.
            </Typography>
          </Box>
        }

        {row.serials.length > 0 &&
        <>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-600)', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Serial Items
            </Typography>
            <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...TH, py: '8px' }}>Serial #</TableCell>
                    <TableCell sx={{ ...TH, py: '8px' }}>Status</TableCell>
                    <TableCell sx={{ ...TH, py: '8px' }}>Ticket #</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.serials.map((s) =>
                <TableRow key={s.id} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                      <TableCell sx={{ ...TD, height: 40, fontFamily: 'monospace', letterSpacing: '0.02em', fontSize: '0.8125rem' }}>
                        {s.serialNumber}
                      </TableCell>
                      <TableCell sx={{ ...TD, height: 40 }}>
                        <StatusBadge status={s.status} />
                      </TableCell>
                      <TableCell sx={{ ...TD, height: 40, color: s.ticketNumber ? 'var(--grey-700)' : 'var(--grey-400)', fontSize: '0.8125rem' }}>
                        {s.ticketNumber ?? '—'}
                      </TableCell>
                    </TableRow>
                )}
                </TableBody>
              </Table>
            </Box>
          </>
        }
      </DialogContent>
    </Dialog>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PortalInventoryPage() {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedRow, setSelectedRow] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location') ?? 'All');
  const [search, setSearch] = useState('');

  // Active On-Site locations for this customer
  const onSiteWarehouses = useMemo(
    () => warehouses.filter((w) => w.customerName === currentUser.customerName && w.type === 'On-Site' && w.active),
    [currentUser.customerName]
  );

  const onSiteNames = useMemo(() => onSiteWarehouses.map((w) => w.name), [onSiteWarehouses]);

  const selectedWarehouse = useMemo(
    () => locationFilter === 'All' ? null : onSiteWarehouses.find((w) => w.name === locationFilter),
    [locationFilter, onSiteWarehouses]
  );

  // Stock rows joined with part details + serial availability
  const myStock = useMemo(() => {
    if (!onSiteNames.length) return [];
    const activeNames = locationFilter === 'All' ? onSiteNames : [locationFilter];
    return stockLevels
    .filter((s) => activeNames.includes(s.locationName))
    .map((s) => {
      const part = parts.find((p) => p.partNumber === s.partNumber);
      const rowSerials = serialItems.filter((si) => si.partNumber === s.partNumber && si.locationName === s.locationName);
      const qtyAvailable = rowSerials.length > 0 ?
      rowSerials.filter((si) => si.status === 'Available').length :
      s.quantityOnHand;
      return {
        ...s,
        description: part?.description ?? '—',
        category: part?.category ?? '—',
        reorderThreshold: part?.reorderThreshold ?? 0,
        serials: rowSerials,
        qtyAvailable
      };
    });
  }, [onSiteNames, locationFilter]);

  // Summary stats
  const totalUnits = myStock.reduce((acc, s) => acc + s.quantityOnHand, 0);
  const availableUnits = myStock.reduce((acc, s) => acc + s.qtyAvailable, 0);
  const lowStockCount = myStock.filter((s) => s.qtyAvailable < s.reorderThreshold).length;

  // Category tabs
  const categoryTabs = useMemo(() => {
    const cats = [...new Set(myStock.map((s) => s.category))].sort();
    return [{ label: 'All', value: 'All' }, ...cats.map((c) => ({ label: c, value: c }))];
  }, [myStock]);

  // Filtered rows
  const displayStock = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = categoryFilter === 'All' ? myStock : myStock.filter((s) => s.category === categoryFilter);
    if (!q) return base;
    return base.filter((s) =>
    s.partNumber.toLowerCase().includes(q) ||
    s.description.toLowerCase().includes(q) ||
    s.serials.some((si) => si.serialNumber.toLowerCase().includes(q))
    );
  }, [myStock, categoryFilter, search]);

  const subtitle = !onSiteWarehouses.length ?
  undefined :
  locationFilter === 'All' ?
  `All On-Site Locations (${onSiteWarehouses.length})` :
  selectedWarehouse ?
  `${selectedWarehouse.name} · ${selectedWarehouse.city}, ${selectedWarehouse.state}` :
  undefined;

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PartDetailModal row={selectedRow} onClose={() => setSelectedRow(null)} />

      <BreadcrumbHeader
        title="On-Site Inventory"
        subtitle={subtitle}
        crumbs={[{ label: 'Portal' }, { label: 'On-Site Inventory' }]} />

      {/* Summary bar */}
      <ContentCard sx={{ display: 'flex', gap: 3, px: 3, py: 2 }}>
        {[
        { label: 'Total Parts', value: myStock.length },
        { label: 'Total Units', value: totalUnits },
        { label: 'Available Units', value: availableUnits },
        { label: 'Below Threshold', value: lowStockCount, alert: lowStockCount > 0 }]
        .map((s) =>
        <Box key={s.label} sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: s.alert ? '#C62828' : 'var(--grey-900)' }}>
              {s.value}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>{s.label}</Typography>
          </Box>
        )}
      </ContentCard>

      {!onSiteWarehouses.length ?
      <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', p: 3 }}>
          <EmptyState title="No on-site location found" description="Your account doesn't have an on-site inventory location configured." />
        </Box> :

      <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {onSiteWarehouses.length > 1 &&
          <FormControl size="small" sx={{ minWidth: 260, ...fieldSx }}>
                <Select
              value={locationFilter}
              onChange={(e) => {setLocationFilter(e.target.value);setCategoryFilter('All');}}
              displayEmpty>
              
                  <MenuItem value="All" sx={{ fontSize: '0.875rem' }}>All On-Site Locations</MenuItem>
                  {onSiteWarehouses.map((w) =>
              <MenuItem key={w.id} value={w.name} sx={{ fontSize: '0.875rem' }}>{w.name}</MenuItem>
              )}
                </Select>
              </FormControl>
          }
            <TextField
            placeholder="Search by Part #, Description or Serial #…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 240, ...fieldSx }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} /></InputAdornment> }} />
          
          </Box>

          {/* Category tabs */}
          <FilterTabs tabs={categoryTabs} value={categoryFilter} onChange={setCategoryFilter} />

          {/* Table */}
          <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
            {displayStock.length === 0 ?
          <Box sx={{ p: 3 }}>
                <EmptyState title="No inventory found" description="No parts match the selected filter." />
              </Box> :

          <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={TH}>Part #</TableCell>
                    <TableCell sx={TH}>Description</TableCell>
                    <TableCell sx={TH}>Category</TableCell>
                    <TableCell sx={TH}>Location</TableCell>
                    <TableCell sx={TH}>Available</TableCell>
                    <TableCell sx={TH}>On Hand</TableCell>
                    <TableCell sx={TH}>Threshold</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayStock.map((row) => {
                const isLow = row.qtyAvailable < row.reorderThreshold;
                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => setSelectedRow(row)}
                    sx={{ cursor: 'pointer' }}>
                    
                        <TableCell sx={{ ...TD, color: '#009688', fontWeight: 500 }}>{row.partNumber}</TableCell>
                        <TableCell sx={TD}>{row.description}</TableCell>
                        <TableCell sx={TD}>{row.category}</TableCell>
                        <TableCell sx={TD}>{row.locationName}</TableCell>
                        <TableCell sx={TD}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: isLow ? '#C62828' : 'var(--grey-900)' }}>
                              {row.qtyAvailable}
                            </Typography>
                            {isLow && <WarningAmberIcon sx={{ fontSize: 14, color: '#E65100' }} />}
                          </Box>
                        </TableCell>
                        <TableCell sx={TD}>{row.quantityOnHand}</TableCell>
                        <TableCell sx={{ ...TD, color: 'var(--grey-500)' }}>{row.reorderThreshold}</TableCell>
                      </TableRow>);

              })}
                </TableBody>
              </Table>
          }
          </Box>
        </ContentCard>
      }
    </Box>);

}